import { useCallback, useMemo } from 'react';

/**
 * Custom hook for handling complex range selector positioning calculations
 *
 * @param {Object} config - Configuration object
 * @param {number[]} config.values - Array of data values for histogram
 * @param {number} config.histogramWidth - Width of histogram in pixels
 * @param {number} config.barWidth - Width of individual histogram bars
 * @param {number} config.barSpacing - Spacing between histogram bars
 * @param {number} config.histogramLeftOffset - Left offset of histogram container
 * @param {number|null} config.minValue - Current minimum value
 * @param {number|null} config.maxValue - Current maximum value
 *
 * @returns {Object} Positioning utilities and calculated values
 */
const useRangePositioning = ({
  values = [],
  histogramWidth = 312,
  barWidth = 3,
  barSpacing = 1,
  histogramLeftOffset = 10,
  minValue = null,
  maxValue = null
}) => {
  // Calculate data bounds
  const dataBounds = useMemo(() => {
    if (values.length === 0) return { min: 0, max: 100 };
    return {
      min: Math.min(...values),
      max: Math.max(...values)
    };
  }, [values]);

  // Calculate histogram configuration
  const histogramConfig = useMemo(() => {
    const bins = Math.floor(histogramWidth / (barWidth + barSpacing));
    const minPercentLimit = (barWidth / 2 / histogramWidth) * 100;
    const maxPercentLimit =
      ((histogramWidth - barWidth / 2) / histogramWidth) * 100;

    return {
      bins,
      minPercentLimit,
      maxPercentLimit,
      binSize: (dataBounds.max - dataBounds.min) / bins
    };
  }, [histogramWidth, barWidth, barSpacing, dataBounds]);

  // Calculate histogram data for visualization
  const histogramData = useMemo(() => {
    if (values.length === 0) return [];

    const { bins, binSize } = histogramConfig;
    const histogram = new Array(bins).fill(0);

    // Handle edge case when all values are the same (binSize = 0)
    if (binSize === 0) {
      // Put all values in the first bin
      histogram[0] = values.length;
    } else {
      values.forEach((value) => {
        const binIndex = Math.min(
          Math.floor((value - dataBounds.min) / binSize),
          bins - 1
        );
        histogram[binIndex]++;
      });
    }

    const maxCount = Math.max(...histogram);
    // Ensure all bars are visible with at least 5% height
    return histogram.map((count) => Math.max((count / maxCount) * 100, 5));
  }, [values, dataBounds, histogramConfig]);

  // Convert value to percentage position (constrained to bar centers)
  const valueToPercent = useCallback(
    (value) => {
      if (value === null || dataBounds.max === dataBounds.min) return null;

      const rawPercent =
        ((value - dataBounds.min) / (dataBounds.max - dataBounds.min)) * 100;
      const { minPercentLimit, maxPercentLimit } = histogramConfig;

      return (
        minPercentLimit +
        (rawPercent / 100) * (maxPercentLimit - minPercentLimit)
      );
    },
    [dataBounds, histogramConfig]
  );

  // Convert percentage position to value (constrained to bar centers)
  const percentToValue = useCallback(
    (percent) => {
      if (dataBounds.max === dataBounds.min) return dataBounds.min;

      const { minPercentLimit, maxPercentLimit } = histogramConfig;
      const normalizedPercent =
        ((percent - minPercentLimit) / (maxPercentLimit - minPercentLimit)) *
        100;

      return (
        dataBounds.min +
        (normalizedPercent / 100) * (dataBounds.max - dataBounds.min)
      );
    },
    [dataBounds, histogramConfig]
  );

  // Calculate pixel positions for handles
  const pixelPositions = useMemo(() => {
    const { minPercentLimit, maxPercentLimit } = histogramConfig;

    const minPercent =
      minValue !== null ? valueToPercent(minValue) : minPercentLimit;
    const maxPercent =
      maxValue !== null ? valueToPercent(maxValue) : maxPercentLimit;

    const firstBarCenterPx = histogramLeftOffset + barWidth / 2;
    const lastBarCenterPx = histogramLeftOffset + histogramWidth - barWidth / 2;

    const minPosPx =
      minValue !== null
        ? (minPercent / 100) * histogramWidth + histogramLeftOffset
        : firstBarCenterPx;

    const maxPosPx =
      maxValue !== null
        ? (maxPercent / 100) * histogramWidth + histogramLeftOffset
        : lastBarCenterPx;

    return {
      minPercent,
      maxPercent,
      minPosPx,
      maxPosPx,
      firstBarCenterPx,
      lastBarCenterPx
    };
  }, [
    minValue,
    maxValue,
    valueToPercent,
    histogramConfig,
    histogramLeftOffset,
    histogramWidth,
    barWidth
  ]);

  // Utility function to convert pixel position to constrained value
  const pixelToConstrainedValue = useCallback(
    (pixelX, trackRect) => {
      const rawPercent = ((pixelX - trackRect.left) / trackRect.width) * 100;
      const { minPercentLimit, maxPercentLimit } = histogramConfig;

      const constrainedPercent = Math.max(
        minPercentLimit,
        Math.min(maxPercentLimit, rawPercent)
      );

      return Math.round(percentToValue(constrainedPercent));
    },
    [percentToValue, histogramConfig]
  );

  // Validation functions
  const isValidMinValue = useCallback(
    (value) => {
      if (value === null) return true;
      const maxLimit = maxValue !== null ? maxValue : dataBounds.max;
      return value >= dataBounds.min && value <= maxLimit;
    },
    [maxValue, dataBounds]
  );

  const isValidMaxValue = useCallback(
    (value) => {
      if (value === null) return true;
      const minLimit = minValue !== null ? minValue : dataBounds.min;
      return value <= dataBounds.max && value >= minLimit;
    },
    [minValue, dataBounds]
  );

  return {
    // Data and configuration
    dataBounds,
    histogramConfig,
    histogramData,

    // Conversion utilities
    valueToPercent,
    percentToValue,
    pixelToConstrainedValue,

    // Calculated positions
    pixelPositions,

    // Validation utilities
    isValidMinValue,
    isValidMaxValue,

    // Helper values for external use
    minPercentLimit: histogramConfig.minPercentLimit,
    maxPercentLimit: histogramConfig.maxPercentLimit
  };
};

export default useRangePositioning;
